import { Log } from '../../../AuxillaryService/Log';
import { asyncTimeout } from '../../../Utility/AsyncTimeout';
import { VideoLoadError } from '../errors';
import { GenericHandlerConfig, GenericVideo, GenericVideoHandler } from '../Generic';

export type GenericStreamHandlerConfig = GenericHandlerConfig & {
    /**
     * The amount in milliseconds to wait before timeout
     */
    streamTimeout?: number;
};

export abstract class GenericStreamHandler extends GenericVideoHandler {
    #streamTimeout: number;

    constructor(config: GenericStreamHandlerConfig) {
        super(config);
        this.#streamTimeout = config.streamTimeout ?? 15_000;
    }

    protected async _createNewPlayer(): Promise<GenericVideo> {
        const player = await super._createNewPlayer();

        player.classList.add('video-stream');

        return player;
    }

    /**
     * @throws {VideoLoadError} when there is an error loading the video
     */
    protected async _loadVideo(player: HTMLVideoElement): Promise<void> {
        const loadTime = Date.now();
        let loaded = false;

        while (loaded === false) {
            try {
                await super._loadVideo(player);
                loaded = true;
                break;
            } catch (err) {
                if (err instanceof VideoLoadError) {
                    const duration = Date.now() - loadTime;

                    if (this.#streamTimeout > 0 && duration > this.#streamTimeout) {
                        Log.Error(
                            `[${
                                this.id
                            }] Failed to play stream after ${duration} milliseconds. ${String(
                                err
                            )}`,
                            null,
                            __fileline
                        );
                        throw err;
                    }

                    /*
                    This delay is meant to prevent spamming the stream if we can't connect.

                    This delay might not be as necessary as originally thought. The BrightSign
                    platform already has a roughly 6 second timeout before erroring out. So this
                    delay will be in addition to the platform's timeout.

                    However we do not know if that will be the same on other platforms. So it might
                    be best to keep the delay for now.
                    */
                    let delay;
                    if (duration < 5_000) {
                        delay = 200;
                    } else if (duration < 10_000) {
                        delay = 500;
                    } else {
                        delay = 1000;
                    }
                    Log.Debug(
                        `[${this.id}] Cannot play stream, will try again in ${delay}ms. ${String(
                            err
                        )}`,
                        null,
                        __fileline
                    );
                    await asyncTimeout(delay);
                } else {
                    throw err;
                }
            }
        }
    }
}
